import { gql, useMutation } from '@apollo/client';
import { useCallback } from 'react';

import { DuplicateRequest } from '@work4all/models/lib/Classes/DuplicateRequest.entity';
import { DuplicateResponse } from '@work4all/models/lib/Classes/DuplicateResponse.entity';
import { DuplicateEntityType } from '@work4all/models/lib/Enums/DuplicateEntityType.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

const CLONE_ENTITY_MUTATION = gql`
  mutation DuplicateEntity($request: DuplicateRequest!) {
    duplicateEntity(request: $request) {
      errorMessage
    }
  }
`;

interface DuplicateVariables {
  request: DuplicateRequest;
}

export const CLONEABLE_ENTITIES = [
  Entities.appointment,
  Entities.note,
  Entities.callMemo,
  Entities.task,
  Entities.salesOpportunities,
  Entities.article,
] as const;

const SUFFIXABLE_ENTITIES = [Entities.article];
const ENTITIES_WITH_ATTACHMENTS = [
  Entities.note,
  Entities.appointment,
  Entities.task,
  Entities.callMemo,
  Entities.salesOpportunities,
];

type Entity = (typeof CLONEABLE_ENTITIES)[number];

const DUPLICATE_ENTITY_TYPE_BY_ENTITY: {
  [key in Entity]: DuplicateEntityType;
} = {
  [Entities.note]: DuplicateEntityType.NOTE,
  [Entities.appointment]: DuplicateEntityType.APPOINTMENT,
  [Entities.task]: DuplicateEntityType.TASK,
  [Entities.callMemo]: DuplicateEntityType.CALL_MEMO,
  [Entities.salesOpportunities]: DuplicateEntityType.SALES_OPPORTUNITY,
  [Entities.article]: DuplicateEntityType.ARTICLE,
};

interface CloneProps {
  id: number;
  entity: Entities;
}

export const useCloneEntityMutation = () => {
  const [cloneEntityMutation] = useMutation<
    DuplicateResponse,
    DuplicateVariables
  >(CLONE_ENTITY_MUTATION);

  const clone = useCallback(
    ({ id, entity }: CloneProps) => {
      const sourceEntityType = DUPLICATE_ENTITY_TYPE_BY_ENTITY[entity];

      if (!sourceEntityType) {
        throw new Error('Invalid source entity type');
      }

      return cloneEntityMutation({
        variables: {
          request: {
            sourceEntityCode: id,
            sourceEntityType,
            includeAttachements: ENTITIES_WITH_ATTACHMENTS.includes(entity),
            textSuffix: SUFFIXABLE_ENTITIES.includes(entity)
              ? ' (2)'
              : undefined,
          },
        },
      });
    },
    [cloneEntityMutation]
  );

  return clone;
};
